import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router'

import { GlobalModalTypes } from 'src/components/GlobalModals/modalTypes'
import { Loader } from 'src/components/shared/Loader'
import config from 'src/config'
import { fixedAssets, mobileAssets } from 'src/containers/PreCache'
import { showGlobalModal } from 'src/ducks/layout'
import routes from 'src/routes'
import Amplitude from 'src/utils/amplitudeHelper'
import { useAuditoriumController } from 'src/utils/useAuditoriumController'
import { useAutoMute } from 'src/utils/useAutoMute'

const MobileAuditorium = ({ currentUser }) => {
  useAutoMute()
  const hasAuditoriumAccess = useAuditoriumController(currentUser)
  const dispatch = useDispatch()
  const history = useHistory()

  useEffect(() => {
    document.title = `${config.eventName} | Auditorium`
    Amplitude.visit('Auditorium')
  }, [])

  if (!hasAuditoriumAccess) {
    return <Loader />
  }

  return (
    <Page>
      <div className="page-shell">
        <img className="masthead" src={mobileAssets.lobbyTitle} alt="National Tax Conference 2026" />

        <div className="auditorium-panel">
          <div className="content-card">
            <button
              className="home-button"
              type="button"
              onClick={() => history.push(routes.lobby)}
              aria-label="Return to the lobby"
            >
              <img src={fixedAssets.homeButtonIcon} alt="" aria-hidden="true" />
            </button>

            <div className="video-container">
              <iframe
                title="auditorium-stream-mobile"
                id="1b1f6ca6-faef-317f-74bf-a4ce68f8e2d6-live-507a30e6-8cf4-adb9-bbb4-5711d8ba79da"
                src={config.HALL_VIDEO_URL}
                frameBorder="0"
                scrolling="no"
                allow="autoplay; encrypted-media; fullscreen"
                allowFullScreen
              />
            </div>

            <div className="controls" aria-label="Auditorium options">
              <button type="button" onClick={() => dispatch(showGlobalModal(GlobalModalTypes.Agenda))}>
                Programme
              </button>
              <button type="button" onClick={() => dispatch(showGlobalModal(GlobalModalTypes.Speakers))}>
                Speakers
              </button>
            </div>
          </div>

          <div className="slido-container">
            <iframe src={config.SLIDO_URL} frameBorder="0" title="Slido" />
          </div>
        </div>
      </div>
    </Page>
  )
}

const Page = styled.main`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  background: url(${mobileAssets.dashboardBg}) center / cover no-repeat fixed;
  font-family: 'Din', 'DIN', sans-serif;

  .page-shell {
    position: relative;
    width: 100%;
    max-width: 393px;
    min-height: 906px;
    margin: 0 auto;
    padding-top: 40px;
    padding-bottom: 77px;
    box-sizing: border-box;
  }

  .masthead {
    display: block;
    width: calc(100% - 40px);
    max-width: 337px;
    height: 174px;
    margin-left: 20px;
    object-fit: contain;
    object-position: top left;
  }

  .auditorium-panel {
    width: 100%;
    margin-top: 34px;
  }

  .content-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    width: calc(100% - 23px);
    max-width: 370px;
    margin: 0 auto;
  }

  button {
    font-family: inherit;
  }

  .home-button {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    padding: 8px;
    overflow: hidden;
    border: 0;
    border-radius: 10px;
    background: var(--Gold-Gradient-01);
    box-shadow: 0 4px 6.15px rgba(0, 0, 0, 0.21);
    color: #fff;
    cursor: pointer;

    img {
      width: 24px;
      height: 24px;
      object-fit: contain;
    }
  }

  .video-container {
    width: 100%;
    height: 180px;
    overflow: hidden;
    background: #000;

    iframe {
      display: block;
      width: 100%;
      height: 100%;
      border: 0;
    }
  }

  .controls {
    display: flex;
    align-items: center;
    gap: 20px;
    width: 100%;

    button {
      flex: 1 1 0;
      min-width: 0;
      padding: 16px 12px;
      border: 0;
      border-radius: 10px;
      background: var(--Gold-Gradient-01);
      box-shadow: 0 4px 6.15px rgba(0, 0, 0, 0.21);
      color: #fff;
      font-size: 18px;
      font-weight: 700;
      line-height: 1;
      text-transform: uppercase;
      cursor: pointer;
    }
  }

  .home-button,
  .controls button {
    transition: transform 150ms ease, box-shadow 150ms ease, opacity 150ms ease;

    &:active {
      transform: translateY(1px);
      box-shadow: 0 2px 3px rgba(0, 0, 0, 0.2);
    }

    &:focus-visible {
      box-shadow: 0 0 0 3px #fff, 0 0 0 5px #ae8341, 0 4px 6.15px rgba(0, 0, 0, 0.21);
    }
  }

  .slido-container {
    width: 100%;
    height: 330px;
    margin-top: 26px;
    overflow: hidden;
    background: #1b1b1b;

    iframe {
      display: block;
      width: 100%;
      height: 100%;
      border: 0;
    }
  }

  @media (hover: hover) {
    .home-button:hover,
    .controls button:hover {
      transform: translateY(-2px);
    }
  }

  @media screen and (max-width: 360px) {
    .controls {
      gap: 12px;

      button {
        font-size: 16px;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .home-button,
    .controls button {
      transition: none;
    }
  }
`

export default MobileAuditorium
